import { useCallback, useEffect, useMemo, useState } from "react";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useMediaTranscript } from "@circle-react-uikit/TranscriptsPlayer/useMediaTranscript";

interface UseVideoTranscriptProps {
  mediaTranscriptId?: number;
}

export const useVideoTranscript = ({
  mediaTranscriptId,
}: UseVideoTranscriptProps) => {
  const [captionsUrl, setCaptionsUrl] = useState<string | undefined>();
  const [currentMediaTranscriptId, setCurrentMediaTranscriptId] = useState<
    number | undefined
  >(mediaTranscriptId);

  const { currentCommunityMember } = usePunditUserContext();

  const { webvtt, parsedWebvtt, mediaTranscript, refetch } = useMediaTranscript(
    currentMediaTranscriptId ?? null,
  );

  const shouldDisplayTranscript =
    !!mediaTranscript && mediaTranscript.enabled && mediaTranscript.is_ready;
  const shouldDisplayNotification =
    !mediaTranscriptId && isCommunityAdmin(currentCommunityMember);

  useEffect(() => {
    if (!webvtt || !shouldDisplayTranscript) {
      setCaptionsUrl(undefined);
      return;
    }

    const blob = new Blob([webvtt], { type: "text/vtt" });
    const blobUrl = URL.createObjectURL(blob);

    setCaptionsUrl(blobUrl);

    return () => URL.revokeObjectURL(blobUrl);
  }, [webvtt, shouldDisplayTranscript]);

  const cues = useMemo(() => parsedWebvtt?.cues || [], [parsedWebvtt]);

  const updateMediaTranscript = useCallback(
    (id: number) => {
      if (currentMediaTranscriptId !== id) {
        setCurrentMediaTranscriptId(id);
      } else {
        void refetch();
      }
    },
    [currentMediaTranscriptId, refetch],
  );

  return {
    mediaTranscript,
    captionsUrl,
    cues,
    shouldDisplayTranscript,
    shouldDisplayNotification,
    updateMediaTranscript,
  };
};
